// web/src/features/event/utils/rebaseEventZone.ts
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  rebaseInstantToZone,
  type RebaseInstantDeps,
} from '@sportninja/common/features/event/rebaseInstantToZone'

// `.tz()` below needs these. Idempotent, and registering here keeps this
// module standalone rather than relying on another file's import side effect.
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Web's dayjs binding for the shared wall-clock rebase rule.
 *
 * `common/` owns the RULE (hold the wall-clock, change the zone) and stays free
 * of any date library; this supplies the conversion.
 */
const deps: RebaseInstantDeps = {
  readWallClock: (iso, zone) => {
    const zoned = dayjs.utc(iso).tz(zone)

    if (!zoned.isValid()) {
      return null
    }

    return {
      year: zoned.year(),
      // dayjs months are 0-indexed; the shared shape uses calendar months.
      month: zoned.month() + 1,
      day: zoned.date(),
      hour: zoned.hour(),
      minute: zoned.minute(),
      second: zoned.second(),
    }
  },
  writeWallClock: (wallClock, zone) => {
    const pad = (value: number) => String(value).padStart(2, '0')
    const formatted =
      `${wallClock.year}-${pad(wallClock.month)}-${pad(wallClock.day)}` +
      `T${pad(wallClock.hour)}:${pad(wallClock.minute)}:${pad(
        wallClock.second
      )}`

    return dayjs.tz(formatted, zone).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
  },
}

/**
 * A stored UTC instant re-interpreted so the wall-clock the admin typed in
 * `fromZone` reads identically in `toZone`.
 *
 * Empty values, a missing zone, or matching zones pass through untouched, so
 * callers can map this over every date field unconditionally.
 */
export const rebaseEventZone = (
  iso: string,
  fromZone: string,
  toZone: string
): string => rebaseInstantToZone(iso, fromZone, toZone, deps)
